import { useMemo, useState, useEffect, useCallback } from "react";
import { Grid, useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { DEFAULT_LAYOUTS, SWITCH_OPTIONS } from "./layouts";
import { WIDGETS } from "./widgetRegistry";
import TileFrame from "./TileFrame";
import {
  getWidgetOrder,
  saveWidgetOrder,
  setWidgetOrderLocal,
  widgetOrderStateKey,
} from "../../../redux/slice/widget-order";

const storageKey = (roleKey, layoutScope) =>
  layoutScope
    ? `myperf_layout_${roleKey}_${layoutScope}`
    : `myperf_layout_${roleKey}`;

/** Legacy: "My Performance" lived in the grid; it is now fixed above the dashboard for reps. */
function normalizeTileType(type, roleKey) {
  if (roleKey === "SALESREP" && type === "myPerformance") {
    return "startTracking";
  }
  return type;
}

/** For scoped tab layouts, drop saved types that are no longer allowed (product default changes). */
function resolveTileTypeForMerge({
  savedType,
  defaultTile,
  roleKey,
  allowedTypesSet,
}) {
  const normalized = normalizeTileType(
    savedType || defaultTile.type,
    roleKey,
  );
  // Stale layout fallback: a saved type that is no longer in the registry
  // (a removed widget, e.g. the old RBD "startTracking") or outside the
  // allowed set for this scoped tab is discarded in favour of the default.
  if (!WIDGETS[normalized]) {
    return defaultTile.type;
  }
  if (allowedTypesSet && !allowedTypesSet.has(normalized)) {
    return defaultTile.type;
  }
  return normalized;
}

/**
 * Each scoped tab shows at most one of each widget type. Merge + coercion can
 * duplicate a type when one slot keeps a valid swap and another is coerced to
 * the same default. Default layout types are preferred when resolving duplicates;
 * valid extra swap-only types are preserved when not duplicated.
 */
function dedupeScopedWidgetTypes(
  tiles,
  layoutDefaultTypes,
  allAllowedTypes = layoutDefaultTypes,
) {
  if (!Array.isArray(tiles) || !tiles.length) return tiles;
  if (!Array.isArray(layoutDefaultTypes) || !layoutDefaultTypes.length) {
    return tiles;
  }
  const defaultSet = new Set(layoutDefaultTypes);
  const allowedSet = new Set(allAllowedTypes);
  const used = new Set();
  return tiles.map((tile) => {
    let type = tile.type;
    const isAllowed = allowedSet.has(type);
    const isUnique = !used.has(type);

    if (!isAllowed || !isUnique) {
      type =
        layoutDefaultTypes.find((t) => !used.has(t)) ??
        allAllowedTypes.find((t) => !used.has(t) && !defaultSet.has(t)) ??
        allAllowedTypes.find((t) => !used.has(t)) ??
        type;
    }
    used.add(type);
    return { ...tile, type };
  });
}

// A single shared empty array, so the "no saved layout" case has a stable
// identity and cannot retrigger effects that depend on it.
const EMPTY_SAVED_WIDGETS = [];

export default function DashboardRenderer({
  roleKey,
  widgetProps,
  /** When set, layout is persisted under a separate Cosmos doc + Redux key (e.g. My Performance tab). */
  layoutScope = null,
  /** Replaces DEFAULT_LAYOUTS[roleKey] when provided (requires layoutScope for persistence). */
  defaultLayoutOverride = null,
  /** Replaces SWITCH_OPTIONS[roleKey] when provided. */
  switchOptionsOverride = null,
  /** Swap-only widgets merged into the switch menu (not default layout tiles). */
  extraSwitchOptionsOverride = null,
  /** Widget types with no data yet: hidden as grid tiles and shown disabled in the switch menu. */
  unavailableWidgetTypes = null,
}) {
  const dispatch = useDispatch();
  const { widgetOrderByRole } = useSelector((state) => state.widgetOrder);

  const isIpadOrBelow = useMediaQuery("(max-width:1024px)");

  const orderKey = widgetOrderStateKey(roleKey, layoutScope);

  /** After any GET for this key (success or 404), Redux stores an array — skip refetch on tab remount. */
  const hasFetchedLayoutOrder = useSelector(
    (state) => orderKey in (state.widgetOrder?.widgetOrderByRole || {}),
  );

  const defaultLayout = useMemo(() => {
    const useTabOverride =
      Boolean(layoutScope?.trim()) &&
      Array.isArray(defaultLayoutOverride) &&
      defaultLayoutOverride.length;
    if (useTabOverride) return defaultLayoutOverride;
    return DEFAULT_LAYOUTS[roleKey] || [];
  }, [roleKey, defaultLayoutOverride, layoutScope]);

  const layoutDefaultTypes = useMemo(
    () => defaultLayout.map((tile) => tile.type),
    [defaultLayout],
  );

  const allScopedSwitchTypes = useMemo(() => {
    if (!Array.isArray(switchOptionsOverride) || !switchOptionsOverride.length) {
      return layoutDefaultTypes;
    }
    const extras = Array.isArray(extraSwitchOptionsOverride)
      ? extraSwitchOptionsOverride
      : [];
    const merged = [...switchOptionsOverride];
    extras.forEach((type) => {
      if (!merged.includes(type)) merged.push(type);
    });
    return merged;
  }, [switchOptionsOverride, extraSwitchOptionsOverride, layoutDefaultTypes]);

  const allowedTypesForScopedLayout = useMemo(() => {
    const hasOverride =
      Array.isArray(switchOptionsOverride) && switchOptionsOverride.length;
    if (!hasOverride) return null;
    return new Set(allScopedSwitchTypes);
  }, [switchOptionsOverride, allScopedSwitchTypes]);

  // NOTE: this is a useEffect dependency below, so its identity must be
  // stable across renders. `|| []` allocated a fresh array on every render
  // whenever the key was absent -- which happens whenever the widget-order
  // GET rejects, since the rejected reducer does not write the key. The
  // effect then re-ran every render and called setLayout with a functional
  // updater, which React cannot eagerly bail out of, so each call scheduled
  // another render: an infinite loop that surfaced as
  // "Maximum update depth exceeded" and blocked the page.
  const savedWidgetsFromApi = widgetOrderByRole?.[orderKey] ?? EMPTY_SAVED_WIDGETS;
  const [layout, setLayout] = useState(defaultLayout);

  useEffect(() => {
    if (!roleKey || hasFetchedLayoutOrder) return;
    dispatch(
      layoutScope
        ? getWidgetOrder({ roleKey, layoutScope })
        : getWidgetOrder(roleKey),
    );
  }, [dispatch, roleKey, layoutScope, hasFetchedLayoutOrder]);

  useEffect(() => {
    if (!defaultLayout.length) {
      setLayout([]);
      return;
    }

    let savedWidgets = [];

    if (Array.isArray(savedWidgetsFromApi) && savedWidgetsFromApi.length) {
      savedWidgets = [...savedWidgetsFromApi];
    } else {
      try {
        const raw = localStorage.getItem(storageKey(roleKey, layoutScope));
        const parsed = raw ? JSON.parse(raw) : [];
        if (Array.isArray(parsed) && parsed.length) {
          savedWidgets = parsed;
        }
      } catch {
        savedWidgets = [];
      }
    }

    let nextLayout = defaultLayout;

    if (savedWidgets.length) {
      const defaultsById = new Map(defaultLayout.map((d) => [d.id, d]));

      const sortedSavedWidgets = [...savedWidgets].sort(
        (a, b) => (a.order || 0) - (b.order || 0),
      );

      const rebuilt = sortedSavedWidgets
        .map((savedItem) => {
          const defaultTile = defaultsById.get(savedItem.id);
          if (!defaultTile) return null;

          return {
            ...defaultTile,
            type: resolveTileTypeForMerge({
              savedType: savedItem.type,
              defaultTile,
              roleKey,
              allowedTypesSet: allowedTypesForScopedLayout,
            }),
          };
        })
        .filter(Boolean);

      const savedIds = new Set(sortedSavedWidgets.map((item) => item.id));
      const missingDefaults = defaultLayout.filter((d) => !savedIds.has(d.id));

      nextLayout = [...rebuilt, ...missingDefaults];
    }

    if (allowedTypesForScopedLayout && layoutDefaultTypes.length) {
      nextLayout = dedupeScopedWidgetTypes(
        nextLayout,
        layoutDefaultTypes,
        allScopedSwitchTypes,
      );
    }

    setLayout((prev) => {
      const prevStr = JSON.stringify(prev);
      const nextStr = JSON.stringify(nextLayout);
      return prevStr === nextStr ? prev : nextLayout;
    });
  }, [
    allowedTypesForScopedLayout,
    defaultLayout,
    roleKey,
    layoutScope,
    savedWidgetsFromApi,
    layoutDefaultTypes,
    allScopedSwitchTypes,
  ]);

  const persistLayout = useCallback(
    (updatedLayout) => {
      const payload = updatedLayout.map((item, index) => ({
        id: item.id,
        type: item.type,
        order: index + 1,
      }));

      dispatch(
        setWidgetOrderLocal({
          roleKey,
          layout: payload,
          layoutScope,
        }),
      );

      try {
        localStorage.setItem(
          storageKey(roleKey, layoutScope),
          JSON.stringify(payload),
        );
      } catch { }

      dispatch(
        saveWidgetOrder({
          roleKey,
          layout: updatedLayout,
          layoutScope,
        }),
      );
    },
    [dispatch, roleKey, layoutScope],
  );

  const handleSwitch = useCallback(
    (tileId, newType) => {
      setLayout((prev) => {
        const updated = [...prev];

        const currentIndex = updated.findIndex((t) => t.id === tileId);
        if (currentIndex === -1) return prev;

        const targetIndex = updated.findIndex((t) => t.type === newType);

        if (targetIndex === -1) {
          updated[currentIndex] = {
            ...updated[currentIndex],
            type: newType,
          };
          persistLayout(updated);
          return updated;
        }

        if (targetIndex !== currentIndex) {
          const temp = updated[currentIndex].type;
          updated[currentIndex].type = updated[targetIndex].type;
          updated[targetIndex].type = temp;
        }

        persistLayout(updated);
        return updated;
      });
    },
    [persistLayout],
  );

  const switchOptions = useMemo(() => {
    if (Array.isArray(switchOptionsOverride) && switchOptionsOverride.length) {
      return allScopedSwitchTypes;
    }
    return SWITCH_OPTIONS[roleKey] || layoutDefaultTypes;
  }, [
    switchOptionsOverride,
    allScopedSwitchTypes,
    roleKey,
    layoutDefaultTypes,
  ]);
  // Hide tiles whose widget has no data yet (kept in `layout`/persisted order,
  // so flipping availability later restores them without a layout migration).
  const visibleLayout = useMemo(
    () =>
      unavailableWidgetTypes
        ? layout.filter((t) => !unavailableWidgetTypes.has(t.type))
        : layout,
    [layout, unavailableWidgetTypes],
  );
  const layoutTypesInUse = useMemo(
    () => visibleLayout.map((t) => t.type),
    [visibleLayout],
  );

  return (
    <Grid container spacing={3}>
      {visibleLayout.map((tile) => {
        const WidgetComponent = WIDGETS[tile.type]?.component;
        if (!WidgetComponent) return null;

        return (
          <Grid
            key={tile.id}
            item
            xs={12}
            md={isIpadOrBelow ? 12 : tile.md}
            lg={isIpadOrBelow ? 12 : tile.lg}
            sx={{ height: tile.height }}
          >
            <TileFrame
              tile={tile}
              switchOptions={switchOptions}
              onSwitch={handleSwitch}
              layoutTypesInUse={layoutTypesInUse}
              unavailableWidgetTypes={unavailableWidgetTypes}
            >
              <WidgetComponent {...(widgetProps[tile.type] || {})} />
            </TileFrame>
          </Grid>
        );
      })}
    </Grid>
  );
}
